import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from "@/lib/utils";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LayoutDashboard, Wand2, LogOut, Bell, Shield, HelpCircle, Check, MessageSquareText, Users, Megaphone } from "lucide-react";
import { VoiceWaveIcon } from "./icons/VoiceWaveIcon";
import SidebarItem from './sidebar/SidebarItem';
import SidebarCollapseToggle from './sidebar/SidebarCollapseToggle';
import { matchesRoute } from '../lib/sidebarState';
import { INTELLIGENCE_PATH } from '../lib/assistantLinks';
import { useAuth } from '../contexts/AuthContext';
import { useAssistantEnabled } from '../hooks/useAssistantEnabled';
import AiLogo from './assistant/AiLogo';
import authService from '../services/authService';
import contractService from '../services/contractService';
import twilioService from '../services/twilioService';

/**
 * The TimeGlobe mark as a sidebar icon. The item's button sizes every SVG icon
 * to 16px (`[&_svg]:size-4`); the mark is an image, so it gets that size here
 * to sit on the same line as its neighbours.
 */
const TimeGlobeMarkIcon = () => <AiLogo className="h-4 w-4" />;

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  onNavigate?: () => void;
  /** Desktop icon rail: icons only, names in tooltips. The mobile sheet never collapses. */
  collapsed?: boolean;
  /** Desktop rail only: shows the collapse toggle at the foot. */
  onToggleCollapsed?: () => void;
  /** Id of the rail element the toggle controls. */
  railId?: string;
}

export function Sidebar({ className, onNavigate, collapsed = false, onToggleCollapsed, railId }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const { enabled: assistantEnabled } = useAssistantEnabled();

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      try {
        const [keyRes, bizRes, contractRes, avvRes, mandatRes, twilioRes] = await Promise.allSettled([
          authService.getBusinessTimeglobeKey(),
          authService.getBusinessInfo(),
          contractService.getContractConfirmation(),
          contractService.getDataProcessingConfirmation(),
          contractService.getLastschriftmandat(),
          twilioService.getLatestNumber(),
        ]);
        const key: any = keyRes.status === 'fulfilled' ? keyRes.value : null;
        const biz: any = bizRes.status === 'fulfilled' ? bizRes.value : null;
        const contract: any = contractRes.status === 'fulfilled' ? contractRes.value : null;
        const avv: any = avvRes.status === 'fulfilled' ? avvRes.value : null;
        const mandat: any = mandatRes.status === 'fulfilled' ? mandatRes.value : null;
        const twilio: any = twilioRes.status === 'fulfilled' ? twilioRes.value : null;

        const hasApiKey = !!key?.timeglobe_auth_key;
        const hasCompanyInfo = !!(biz?.business_name && biz?.street_address && biz?.city && biz?.postal_code && biz?.country && biz?.contact_person && biz?.email && biz?.phone_number && biz?.tax_id);
        const hasAllContracts = !!contract?.confirmed && !!avv?.confirmed && !!mandat;
        const hasTwilio = !!twilio?.phone_number;

        if (!cancelled) setOnboardingComplete(hasApiKey && hasCompanyInfo && hasAllContracts && hasTwilio);
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, location.pathname]);

  const navigationItems = [
    {
      text: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
    },
    // Only where the assistant is switched on for the account; the TimeGlobe
    // mark is its identity everywhere (chat avatar, mode switch).
    ...(assistantEnabled
      ? [{ text: 'TimeGlobe Intelligence', icon: TimeGlobeMarkIcon, path: INTELLIGENCE_PATH, tightLabel: true }]
      : []),
    {
      text: 'Agenten',
      icon: VoiceWaveIcon,
      path: '/voice-agent',
    },
    {
      text: 'Verläufe',
      icon: MessageSquareText,
      path: '/conversations',
    },
    {
      text: 'Kunden',
      icon: Users,
      path: '/customers',
    },
    {
      text: 'Marketing',
      icon: Megaphone,
      path: '/marketing',
      comingSoon: true,
    },
    {
      text: 'Erinnerungen',
      icon: Bell,
      path: '/reminders',
      comingSoon: true,
    },
    {
      text: 'Datenschutz',
      icon: Shield,
      path: '/privacy',
    }
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    if (onNavigate) onNavigate();
  };

  // A page below an entry (a single conversation, a customer) keeps it marked.
  const isActive = (path: string) => matchesRoute(location.pathname, path);

  return (
    <TooltipProvider delayDuration={120} skipDelayDuration={300}>
      <div
        data-state={collapsed ? 'collapsed' : 'expanded'}
        className={cn("flex flex-col h-full w-full", className)}
      >
        <nav aria-label="Hauptnavigation" className="flex-1 py-4">
          <div className="px-3 py-2">
            <div className="space-y-1">
              {navigationItems.map((item) => (
                <SidebarItem
                  key={item.path}
                  label={item.text}
                  icon={item.icon}
                  collapsed={collapsed}
                  active={!item.comingSoon && isActive(item.path)}
                  locked={item.comingSoon}
                  hint={item.comingSoon ? `${item.text} – bald verfügbar` : undefined}
                  tightLabel={'tightLabel' in item && item.tightLabel}
                  onSelect={() => handleNavigation(item.path)}
                />
              ))}
            </div>
          </div>
        </nav>
        <div className="mt-auto pb-4">
          <div className="my-2 border-t border-chrome-border mx-3" />
          <nav aria-label="Konto und Hilfe" className="px-3 py-2">
            <div className="space-y-1">
              <SidebarItem
                label="Onboarding"
                icon={Wand2}
                collapsed={collapsed}
                active={isActive('/onboarding')}
                hint={onboardingComplete ? 'Onboarding – abgeschlossen' : undefined}
                dot={onboardingComplete}
                badge={onboardingComplete && (
                  <span className="inline-flex items-center justify-center rounded-full text-white" style={{ width: '18px', height: '18px', backgroundColor: '#0052F4' }}>
                    <Check strokeWidth={4} style={{ width: '11px', height: '11px' }} />
                    <span className="sr-only">abgeschlossen</span>
                  </span>
                )}
                onSelect={() => handleNavigation('/onboarding')}
              />
              <SidebarItem
                label="Hilfe"
                icon={HelpCircle}
                collapsed={collapsed}
                active={isActive('/help')}
                onSelect={() => handleNavigation('/help')}
              />
              <SidebarItem
                label="Abmelden"
                icon={LogOut}
                collapsed={collapsed}
                tone="cancel"
                onSelect={handleLogout}
              />
            </div>
          </nav>
          {onToggleCollapsed && (
            <div className="px-3 pt-1">
              <SidebarCollapseToggle collapsed={collapsed} onToggle={onToggleCollapsed} controls={railId} />
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default Sidebar;
